import { Link } from "react-router-dom";
import ClassNames from "Helpers/Common";
import { EDIT_PATIENT_PATH } from "Constants/Views";
import { Badge, PaginationFooter, EmptyRow, TableSkeleton } from "../components";
import { formatMins, dateTime } from "../format";

/* Operational triage. A row of count cards (one per CTO alert); clicking a card
   loads that alert's paginated patient/visit list below (master–detail). Nothing
   is selected on open — the list only appears once a card is clicked. */

const money = (n) => (n || n === 0 ? `$${Number(n).toFixed(2)}` : "—");

// Patient name deep-links to the admin patient page (Mongo _id → /patient/edit/:id),
// opened in a new tab so the triage list stays put.
const nameCell = (name, patientId) =>
  patientId ? (
    <Link
      to={EDIT_PATIENT_PATH.replace(":id", patientId)}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-blue-chill hover:underline"
    >
      {name}
    </Link>
  ) : (
    <span className="font-medium sapphire">{name}</span>
  );

const muted = (v) => <span className="text-gray-500">{v}</span>;
const doctorCell = (r) => (r.doctorName ? muted(r.doctorName) : <span className="text-gray-300">unassigned</span>);
// Visit option flags — the same booleans the patient chose at checkout.
const flagsCell = (r) => {
  const f = [
    r.doctorNote && "Doctor Note",
    r.rush && "Rush",
    r.skipChat && "Skip chat",
    r.noAds && "No ads",
    r.addDiagnosis && "Diagnosis",
  ].filter(Boolean);
  if (!f.length) return <span className="text-gray-300">—</span>;
  const text = f.join(" · ");
  return (
    <span className="text-gray-500" title={text}>
      {text}
    </span>
  );
};
const noteCell = (r) => (r.hasNote ? <Badge tone="yellow">unsigned</Badge> : <Badge tone="red">none</Badge>);
const missingCell = (r) => {
  const m = r.missingSections || [];
  return (
    <span className="text-gray-500" title={m.join(", ")}>
      {m.length}/7{m.length ? ` · ${m.slice(0, 2).join(", ")}${m.length > 2 ? "…" : ""}` : ""}
    </span>
  );
};

// Column set per alert key. Each column: { label, render(row) }. Every table
// ends with an absolute "Visit created" date+time (the field each list sorts by).
const COLUMNS = {
  lobbyWait: [
    { label: "Patient", render: (r) => nameCell(r.patientName, r.patientId) },
    { label: "State", render: (r) => muted(r.stateShort || r.state) },
    { label: "Waiting", render: (r) => <span className="text-red-600 font-medium">{formatMins(r.waitingMins)}</span> },
    { label: "Flags", render: flagsCell },
    { label: "Visit created", render: (r) => muted(dateTime(r.createdAt)) },
  ],
  noteNotWritten: [
    { label: "Patient", render: (r) => nameCell(r.patientName, r.patientId) },
    { label: "Doctor", render: doctorCell },
    { label: "Flags", render: flagsCell },
    { label: "Note", render: noteCell },
    { label: "Charge", render: (r) => <span className="sapphire font-medium">{money(r.charge)}</span> },
    { label: "Visit created", render: (r) => muted(dateTime(r.createdAt)) },
  ],
  activeVisitStale: [
    { label: "Patient", render: (r) => nameCell(r.patientName, r.patientId) },
    { label: "Doctor", render: doctorCell },
    { label: "State", render: (r) => muted(r.stateShort || r.state) },
    { label: "Flags", render: flagsCell },
    { label: "Open for", render: (r) => <span className="text-red-600 font-medium">{formatMins(r.openMins)}</span> },
    { label: "Visit created", render: (r) => muted(dateTime(r.createdAt)) },
  ],
  paidNoProfile: [
    { label: "Patient", render: (r) => nameCell(r.patientName, r.patientId) },
    { label: "State", render: (r) => muted(r.stateShort || r.state) },
    { label: "Flags", render: flagsCell },
    { label: "Paid", render: (r) => <span className="sapphire font-medium">{money(r.charge)}</span> },
    { label: "Missing sections", render: missingCell },
    { label: "Visit created", render: (r) => muted(dateTime(r.createdAt)) },
  ],
};

// A single count card — clickable, highlighted when selected.
const AlertCard = ({ label, description, count, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={ClassNames(
      "rounded-lg bg-white flex flex-col px-6 py-4 gap-1 shadow-lg text-left transition focus:outline-none hover:shadow-xl",
      selected && "ring-2 ring-blue-chill"
    )}
  >
    <span className="text-sm font-medium text-gray-500">{label}</span>
    <span className={ClassNames("text-2xl font-semibold", count > 0 ? "text-red-600" : "sapphire")}>
      {count}
    </span>
    <span className="text-xs text-gray-400">{description}</span>
  </button>
);

const AlertTable = ({ columns, rows, data, loading, onPage, emptyText }) => {
  const perPage = data?.perPage || 10;
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / perPage));
  if (loading && rows.length === 0) return <TableSkeleton rows={6} cols={columns.length} />;
  const colWidth = `${100 / columns.length}%`;
  return (
    <div className="rounded-lg bg-white shadow-lg overflow-hidden">
      {/* fixed table-layout makes the <col> widths authoritative, so every
          column is exactly equal regardless of content length (same fix the
          Conversions table uses). Cells truncate rather than reflow. */}
      <table className="min-w-full table-fixed divide-y divide-gray-100" style={{ tableLayout: "fixed" }}>
        <colgroup>
          {columns.map((c) => (
            <col key={c.label} style={{ width: colWidth }} />
          ))}
        </colgroup>
        <thead className="bg-gray-50">
          <tr>
            {columns.map((c) => (
              <th key={c.label} className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wide truncate">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.length === 0 && <EmptyRow colSpan={columns.length} text={emptyText} />}
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50">
              {columns.map((c) => (
                <td key={c.label} className="px-6 py-3 text-sm sapphire truncate">
                  <div className="truncate">{c.render(row)}</div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <PaginationFooter page={data?.page || 1} totalPages={totalPages} total={data?.total || 0} onPage={onPage} />
    </div>
  );
};

export const AlertsTab = ({ summary, detail, selected, onSelect, onPage, loadingDetail }) => {
  const alerts = summary?.alerts || [];
  const selectedDef = alerts.find((a) => a.key === selected);
  const columns = COLUMNS[selected] || [];
  // keepPreviousData can hold a prior alert's rows during a switch — only show
  // rows once the payload matches the selected card, else the table is loading.
  const matched = detail && detail.key === selected;
  const rows = matched ? detail.rows || [] : [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <span className="text-lg font-medium sapphire">Needs attention</span>
        <span className="text-xs text-gray-400">
          Patients and visits stuck in a state that needs a human. Click a card to see the list.
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {alerts.map((a) => (
          <AlertCard
            key={a.key}
            label={a.label}
            description={a.description}
            count={a.count}
            selected={selected === a.key}
            onClick={() => onSelect(a.key)}
          />
        ))}
      </div>

      {selected && (
        <div className="flex flex-col gap-3">
          <div className="flex items-baseline gap-3">
            <span className="text-base font-medium sapphire">{selectedDef?.label}</span>
            {matched && <span className="text-xs text-gray-400">{detail.total} patient{detail.total === 1 ? "" : "s"}</span>}
          </div>
          <AlertTable
            columns={columns}
            rows={rows}
            data={matched ? detail : null}
            loading={loadingDetail || !matched}
            onPage={onPage}
            emptyText="Nothing needs attention here ✓"
          />
        </div>
      )}
    </div>
  );
};
